import React, { useEffect, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Button,
  message,
  Tabs,
  Space,
  Tag,
  Typography,
  Alert,
  Popconfirm,
  Empty,
  Spin,
  Descriptions,
  Tooltip,
} from 'antd';
import {
  WalletOutlined,
  ReloadOutlined,
  SendOutlined,
  SafetyCertificateOutlined,
  LockOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchBalance,
  fetchBalanceHistory,
  fetchWithdrawals,
} from '../store/slices/balanceSlice';
import TreasuryService from '../services/treasury';
import { BalanceService } from '../services/balance';
import { formatAmount } from '../utils/money';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

// 风控等级友好文案（不暴露内部术语）
const TIER_TEXT: Record<string, { label: string; desc: string; color: string }> = {
  tier1: { label: '新商户', desc: '周结算 · 预留 10%', color: 'gold' },
  tier2: { label: '成长商户', desc: '周结算', color: 'blue' },
  tier3: { label: '优质大客户', desc: '日结算', color: 'green' },
};

// 结算周期友好文案
const CYCLE_TEXT: Record<string, string> = {
  daily: '每日结算',
  weekly: '每周结算',
  monthly: '每月结算',
  manual: '手动结算',
  t1: 'T+1 结算',
  t0: '当日结算',
};

// 资金构成块所需的风控数据结构（字段做下划线兼容兜底）
interface RiskCurrencyAmount {
  currency: string;
  amount: number;
}
interface UpcomingRelease {
  currency: string;
  amount: number;
  release_at: string;
}
interface RiskProfile {
  tier?: string;
  settlement_cycle?: string;
  reserve_percent?: number;
  reserve_hold_days?: number;
  reserved_by_currency?: RiskCurrencyAmount[];
  risk_held_by_currency?: RiskCurrencyAmount[];
  upcoming_releases?: UpcomingRelease[];
}

const BalancePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [settling, setSettling] = useState(false);

  // 风控 / 资金构成（独立于 redux，接口未就绪时隐藏整块，不崩）
  const [riskProfile, setRiskProfile] = useState<RiskProfile | null>(null);
  const [riskLoading, setRiskLoading] = useState(true);
  const [riskAvailable, setRiskAvailable] = useState(false);

  const {
    balance,
    history,
    withdrawals,
    loading,
    error,
  } = useAppSelector((state) => state.balance);

  const loadRiskProfile = React.useCallback(async () => {
    setRiskLoading(true);
    try {
      const data = await BalanceService.getRiskProfile();
      if (data && typeof data === 'object') {
        setRiskProfile(data);
        setRiskAvailable(true);
      } else {
        setRiskAvailable(false);
      }
    } catch (err) {
      // 接口 404 / 未就绪：隐藏该块，不打扰商户
      setRiskAvailable(false);
      setRiskProfile(null);
    } finally {
      setRiskLoading(false);
    }
  }, []);

  useEffect(() => {
    dispatch(fetchBalance());
    dispatch(fetchBalanceHistory({ page: 1, limit: 20 }));
    dispatch(fetchWithdrawals({ page: 1, limit: 20 }));
    loadRiskProfile();
  }, [dispatch, loadRiskProfile]);

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  // 一键发起结算（MoR）：后端按所有有可用余额的币种自动创建结算，无需手输 id
  const handleSettle = async () => {
    setSettling(true);
    try {
      await TreasuryService.createMoRSettlement();
      message.success('结算已发起，等待平台审核');
      dispatch(fetchBalance());
      dispatch(fetchWithdrawals({ page: 1, limit: 20 }));
    } catch (err: any) {
      message.error(err?.response?.data?.message || '发起结算失败，请确认已配置结算银行账户');
    } finally {
      setSettling(false);
    }
  };

  const handleRefresh = () => {
    dispatch(fetchBalance());
    dispatch(fetchBalanceHistory({ page: 1, limit: 20 }));
    dispatch(fetchWithdrawals({ page: 1, limit: 20 }));
    loadRiskProfile();
  };

  // 各币种可用余额（来自归一化后的 balances 数组）
  const currencyBalances = balance?.balances || [];
  const hasAvailable = currencyBalances.some((b) => (b.available_balance || 0) > 0);

  // ===== 资金构成（三口径：可用 / 预留冻结 / 风控冻结），按币种聚合 =====
  // 字段做下划线命名兼容兜底：可用优先 available，再回退 available_balance。
  const reservedList = riskProfile?.reserved_by_currency || [];
  const riskHeldList = riskProfile?.risk_held_by_currency || [];
  const upcomingReleases = riskProfile?.upcoming_releases || [];

  const findAmount = (list: RiskCurrencyAmount[], currency: string): number =>
    list.find((x) => x?.currency === currency)?.amount || 0;

  // 汇总所有出现过的币种（余额 + 预留 + 风控）
  const allCurrencies = Array.from(
    new Set([
      ...currencyBalances.map((b) => b.currency),
      ...reservedList.map((x) => x.currency),
      ...riskHeldList.map((x) => x.currency),
    ].filter(Boolean))
  );

  const composition = allCurrencies.map((currency) => {
    const bal = currencyBalances.find((b) => b.currency === currency);
    const available = bal?.available_balance || 0;
    const reserved = findAmount(reservedList, currency);
    const riskHeld = findAmount(riskHeldList, currency);
    return { currency, available, reserved, riskHeld };
  });

  const tierInfo = riskProfile?.tier ? TIER_TEXT[riskProfile.tier] : undefined;
  const hasComposition = composition.some(
    (c) => c.available > 0 || c.reserved > 0 || c.riskHeld > 0
  );

  const historyColumns: ColumnsType<any> = [
    {
      title: '时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => {
        const typeMap: Record<string, { label: string; color: string }> = {
          payment: { label: '支付收入', color: 'green' },
          refund: { label: '退款', color: 'red' },
          withdrawal: { label: '提现', color: 'blue' },
          fee: { label: '手续费', color: 'orange' },
          adjustment: { label: '调整', color: 'blue' },
        };
        const config = typeMap[type] || { label: type, color: 'default' };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number, record: any) => (
        <Text strong style={{ color: amount >= 0 ? '#52c41a' : '#ff4d4f' }}>
          {amount >= 0 ? '+' : ''}
          {record.currency} {(amount / 100).toFixed(2)}
        </Text>
      ),
    },
    {
      title: '余额',
      dataIndex: 'balance_after',
      key: 'balance_after',
      render: (balance: number, record: any) => (
        <Text>
          {record.currency} {(balance / 100).toFixed(2)}
        </Text>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: '关联ID',
      dataIndex: 'reference_id',
      key: 'reference_id',
      render: (text: string) => <Text type="secondary">{text || '-'}</Text>,
    },
  ];

  const withdrawalColumns: ColumnsType<any> = [
    {
      title: '提现时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number, record: any) => (
        <Text strong>
          {record.currency} {(amount / 100).toFixed(2)}
        </Text>
      ),
    },
    {
      title: '手续费',
      dataIndex: 'fee',
      key: 'fee',
      render: (fee: number, record: any) => (
        <Text type="secondary">
          {record.currency} {(fee / 100).toFixed(2)}
        </Text>
      ),
    },
    {
      title: '实际到账',
      dataIndex: 'net_amount',
      key: 'net_amount',
      render: (netAmount: number, record: any) => (
        <Text strong type="success">
          {record.currency} {(netAmount / 100).toFixed(2)}
        </Text>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusMap: Record<string, { label: string; color: string }> = {
          pending: { label: '处理中', color: 'processing' },
          processing: { label: '处理中', color: 'processing' },
          completed: { label: '已完成', color: 'success' },
          failed: { label: '失败', color: 'error' },
          cancelled: { label: '已取消', color: 'default' },
        };
        const config = statusMap[status] || { label: status, color: 'default' };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: '目标账户',
      dataIndex: 'destination_type',
      key: 'destination_type',
      render: (type: string, record: any) => (
        <Space direction="vertical" size="small">
          <Text>{type === 'bank_account' ? '银行账户' : '其他'}</Text>
          {record.destination_details?.account_number && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.destination_details.account_number}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: '处理时间',
      dataIndex: 'processed_at',
      key: 'processed_at',
      render: (text: string) =>
        text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          账户余额
        </Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
            刷新
          </Button>
          <Popconfirm
            title="发起结算"
            description="将把当前所有币种的可用余额结算至您的结算银行账户，提交后等待平台审核。"
            okText="确认发起"
            cancelText="取消"
            onConfirm={handleSettle}
            disabled={!hasAvailable || settling}
          >
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={settling}
              disabled={!hasAvailable}
            >
              一键发起结算
            </Button>
          </Popconfirm>
        </Space>
      </div>

      <Alert
        style={{ marginBottom: 16 }}
        type="info"
        showIcon
        message="结算说明"
        description="平台代收货款并代缴税务，您无需手动输入支付/结算 ID。点击「一键发起结算」即可把各币种可用余额一次性结算到已配置的结算银行账户，平台审核通过后自动出款。"
      />

      {/* 资金构成 / 风控：接口未就绪时整块隐藏，不崩 */}
      {riskLoading ? (
        <Card style={{ marginBottom: 24 }}>
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Spin />
          </div>
        </Card>
      ) : riskAvailable ? (
        <Card
          title={
            <Space>
              <SafetyCertificateOutlined />
              <span>资金构成与账户状态</span>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Alert
            style={{ marginBottom: 16 }}
            type="info"
            showIcon
            message="关于资金预留"
            description={`为保障交易安全，平台会对您的部分收入做滚动预留，${
              riskProfile?.reserve_hold_days ?? 'N'
            } 天后自动释放为可结算资金；个别高风险订单可能被临时冻结，核实后即解除。`}
          />

          {/* 账户状态：风控等级 / 结算周期 / 预留比例 / 释放周期 */}
          <Descriptions
            bordered
            size="small"
            column={{ xs: 1, sm: 2, lg: 4 }}
            style={{ marginBottom: 16 }}
          >
            <Descriptions.Item label="账户等级">
              {tierInfo ? (
                <Space size={4}>
                  <Tag color={tierInfo.color}>{tierInfo.label}</Tag>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {tierInfo.desc}
                  </Text>
                </Space>
              ) : (
                <Text type="secondary">-</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="结算周期">
              {riskProfile?.settlement_cycle
                ? CYCLE_TEXT[riskProfile.settlement_cycle] ||
                  riskProfile.settlement_cycle
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="预留比例">
              {riskProfile?.reserve_percent != null
                ? `${riskProfile.reserve_percent}%`
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="预留释放周期">
              {riskProfile?.reserve_hold_days != null
                ? `${riskProfile.reserve_hold_days} 天`
                : '-'}
            </Descriptions.Item>
          </Descriptions>

          {/* 三口径：可用 / 预留冻结 / 风控冻结（按币种） */}
          {hasComposition ? (
            <Row gutter={[16, 16]}>
              {composition.map((c) => (
                <Col xs={24} lg={12} key={c.currency}>
                  <Card size="small" bordered title={`${c.currency} 资金构成`}>
                    <Row gutter={16}>
                      <Col span={8}>
                        <Statistic
                          title={
                            <Space size={4}>
                              <WalletOutlined />
                              <span>可用余额</span>
                            </Space>
                          }
                          value={c.available / 100}
                          precision={2}
                          valueStyle={{ color: '#2F6BFF', fontSize: 18 }}
                        />
                      </Col>
                      <Col span={8}>
                        <Statistic
                          title={
                            <Tooltip title="为控制风险滚动预留的资金，到期后自动释放为可结算">
                              <Space size={4}>
                                <ClockCircleOutlined />
                                <span>
                                  预留冻结 <InfoCircleOutlined style={{ fontSize: 12 }} />
                                </span>
                              </Space>
                            </Tooltip>
                          }
                          value={c.reserved / 100}
                          precision={2}
                          valueStyle={{ color: '#fa8c16', fontSize: 18 }}
                        />
                      </Col>
                      <Col span={8}>
                        <Statistic
                          title={
                            <Tooltip title="高风险订单核实期间被临时冻结的资金，核实通过后解除">
                              <Space size={4}>
                                <LockOutlined />
                                <span>
                                  风控冻结 <InfoCircleOutlined style={{ fontSize: 12 }} />
                                </span>
                              </Space>
                            </Tooltip>
                          }
                          value={c.riskHeld / 100}
                          precision={2}
                          valueStyle={{ color: '#cf1322', fontSize: 18 }}
                        />
                      </Col>
                    </Row>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <Empty description="暂无预留或冻结资金" />
          )}

          {/* 即将释放的预留 */}
          {upcomingReleases.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Text strong>即将释放的预留</Text>
              <Table
                style={{ marginTop: 8 }}
                size="small"
                rowKey={(r: UpcomingRelease, idx?: number) =>
                  `${r.currency}-${r.release_at}-${idx}`
                }
                dataSource={upcomingReleases}
                pagination={false}
                locale={{ emptyText: '暂无即将释放的预留' }}
                columns={[
                  {
                    title: '释放时间',
                    dataIndex: 'release_at',
                    key: 'release_at',
                    render: (text: string) =>
                      text ? dayjs(text).format('YYYY-MM-DD') : '-',
                  },
                  {
                    title: '币种',
                    dataIndex: 'currency',
                    key: 'currency',
                  },
                  {
                    title: '释放金额',
                    dataIndex: 'amount',
                    key: 'amount',
                    render: (amount: number, record: UpcomingRelease) => (
                      <Text strong type="success">
                        {formatAmount(amount, record.currency)}
                      </Text>
                    ),
                  },
                ]}
              />
            </div>
          )}
        </Card>
      ) : null}

      <Card title="各币种可用余额" loading={loading} style={{ marginBottom: 24 }}>
        {currencyBalances.length > 0 ? (
          <Row gutter={[16, 16]}>
            {currencyBalances.map((b) => (
              <Col xs={24} sm={12} lg={8} key={b.currency}>
                <Card size="small" bordered>
                  <Statistic
                    title={`${b.currency} 可用余额`}
                    value={(b.available_balance || 0) / 100}
                    precision={2}
                    prefix={<WalletOutlined />}
                    suffix={b.currency}
                    valueStyle={{ color: '#2F6BFF' }}
                  />
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary">
                      待结算 {formatAmount(b.pending_balance, b.currency)} · 冻结{' '}
                      {formatAmount(b.frozen_balance, b.currency)}
                    </Text>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <Empty description="暂无可用余额" />
        )}
      </Card>

      <Card>
        <Tabs defaultActiveKey="history">
          <TabPane tab="余额明细" key="history">
            <Table
              columns={historyColumns}
              dataSource={history.data || []}
              loading={history.loading}
              rowKey="id"
              locale={{ emptyText: '暂无数据' }}
              pagination={{
                total: history.pagination?.total || 0,
                current: history.pagination?.current_page || 1,
                pageSize: history.pagination?.per_page || 20,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 条`,
                onChange: (page, pageSize) => {
                  dispatch(fetchBalanceHistory({ page, limit: pageSize }));
                },
              }}
            />
          </TabPane>
          <TabPane tab="结算记录" key="withdrawals">
            <Table
              columns={withdrawalColumns}
              dataSource={withdrawals.data || []}
              loading={withdrawals.loading}
              rowKey="id"
              locale={{ emptyText: '暂无数据' }}
              pagination={{
                total: withdrawals.pagination?.total || 0,
                current: withdrawals.pagination?.current_page || 1,
                pageSize: withdrawals.pagination?.per_page || 20,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 条`,
                onChange: (page, pageSize) => {
                  dispatch(fetchWithdrawals({ page, limit: pageSize }));
                },
              }}
            />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default BalancePage;
